import type { FlagSummary } from '@zuruflex/api-client';
import { intlTagFor, type Locale } from '@zuruflex/i18n';
import { FLAG_ROLLOUT_ALL, type FlagJson, type FlagRule, type FlagState } from '@zuruflex/types';

/** What the list filters on: a flag nobody has saved is neither on nor off. */
export type FlagStatus = 'on' | 'off' | 'untouched';

export function flagStatus(flag: FlagSummary): FlagStatus {
  if (!flag.managed) return 'untouched';
  return flag.state.enabled ? 'on' : 'off';
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Ids as typed — one per line, or comma-separated, which is how they come
 * out of a spreadsheet. Invalid entries are kept rather than dropped, so
 * `validateFlagState` refuses the draft instead of it silently saving a
 * shorter list than the one on screen.
 */
export function parseIdList(text: string): { ids: string[]; invalid: string[] } {
  const ids = text
    .split(/[\s,]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  return { ids, invalid: ids.filter((id) => !UUID.test(id)) };
}

/** Rule ids only need to be unique within a flag and to match `[A-Za-z0-9_-]`. */
export function newRuleId(): string {
  return `r-${Math.random().toString(36).slice(2, 10)}`;
}

/** Basis points in the database, a percentage in the box: 1234 ↔ 12.34. */
export function rolloutToPercent(bps: number): string {
  return String(bps / 100);
}

export function percentToRollout(raw: string): number {
  const pct = Number(raw);
  // NaN reaches the validator, which names the rule that is wrong.
  if (raw.trim() === '' || !Number.isFinite(pct)) return Number.NaN;
  return Math.round(pct * 100);
}

/** A new rule matches everyone and serves the flag's default until edited. */
export function blankRule(value: FlagJson): FlagRule {
  return {
    id: newRuleId(),
    surfaces: [],
    platforms: [],
    minAppVersion: null,
    customerIds: [],
    partnerIds: [],
    partnerUserIds: [],
    rolloutBasisPoints: FLAG_ROLLOUT_ALL,
    value,
    note: null,
  };
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export interface FlagStateDiff {
  enabled: { before: boolean; after: boolean } | null;
  fallthrough: { before: FlagJson; after: FlagJson } | null;
  added: number;
  removed: number;
  changed: number;
  /** True when the only rule change is their order. */
  reordered: boolean;
  rulesBefore: number;
  rulesAfter: number;
}

/**
 * What a save — or a history entry — changed, in the terms somebody
 * confirming it thinks in. Rules are matched by id, so editing a rule reads
 * as one change rather than a removal and an addition.
 */
export function diffFlagStates(before: FlagState | null, after: FlagState | null): FlagStateDiff {
  const beforeRules = before?.rules ?? [];
  const afterRules = after?.rules ?? [];
  const beforeById = new Map(beforeRules.map((rule) => [rule.id, rule]));
  const afterIds = new Set(afterRules.map((rule) => rule.id));

  let added = 0;
  let changed = 0;
  for (const rule of afterRules) {
    const old = beforeById.get(rule.id);
    if (!old) added++;
    else if (!same(old, rule)) changed++;
  }
  const removed = beforeRules.filter((rule) => !afterIds.has(rule.id)).length;

  const keptBefore = beforeRules.filter((rule) => afterIds.has(rule.id)).map((rule) => rule.id);
  const keptAfter = afterRules.filter((rule) => beforeById.has(rule.id)).map((rule) => rule.id);

  const enabledBefore = before?.enabled ?? false;
  const enabledAfter = after?.enabled ?? false;

  return {
    enabled: enabledBefore === enabledAfter ? null : { before: enabledBefore, after: enabledAfter },
    fallthrough:
      before && after && same(before.fallthrough, after.fallthrough)
        ? null
        : before || after
          ? { before: before?.fallthrough ?? null, after: after?.fallthrough ?? null }
          : null,
    added,
    removed,
    changed,
    reordered: !same(keptBefore, keptAfter),
    rulesBefore: beforeRules.length,
    rulesAfter: afterRules.length,
  };
}

/** A value as one short line — `true`, `"beta"`, `{"message":…}`. */
export function formatFlagValue(value: FlagJson, max = 60): string {
  const text = JSON.stringify(value) ?? 'null';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** "3 minutes ago" — history is read for recency first, exact time second. */
export function relativeTime(value: string, locale: Locale, now = Date.now()): string {
  const seconds = Math.round((new Date(value).getTime() - now) / 1000);
  const format = new Intl.RelativeTimeFormat(intlTagFor(locale), { numeric: 'auto' });
  const steps: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  for (const [unit, size] of steps) {
    if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
  }
  return format.format(seconds, 'second');
}
